import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOFloatingCTA from '../components/seo/SEOFloatingCTA';
import { Button } from '../components/ui/button';

const manifestoLines = [
  { text: "Aujourd'hui, pour savoir quelque chose dans votre entreprise,", highlight: false },
  { text: "il faut chercher.", highlight: false, bold: true },
  { text: "", spacer: true },
  { text: "Chercher dans les emails.", highlight: false },
  { text: "Chercher dans le CRM.", highlight: false },
  { text: "Chercher dans les messages.", highlight: false },
  { text: "Appeler quelqu'un.", highlight: false },
  { text: "Attendre.", highlight: false },
  { text: "", spacer: true },
  { text: "Et parfois, malgré tout ça,", highlight: false },
  { text: "ne pas trouver.", highlight: false, bold: true },
  { text: "", spacer: true },
  { text: "", spacer: true },
  { text: "Maintenant, imaginez autre chose.", highlight: false, bold: true, large: true },
  { text: "", spacer: true },
  { text: "Vous posez une question.", highlight: false },
  { text: "Votre entreprise vous répond.", highlight: true, bold: true },
  { text: "", spacer: true },
  { text: "Pas un outil.", highlight: false },
  { text: "Pas un collègue.", highlight: false },
  { text: "Votre entreprise.", highlight: false, bold: true },
  { text: "", spacer: true },
  { text: "Toute l'information.", highlight: false },
  { text: "Tous les échanges.", highlight: false },
  { text: "Tous les collaborateurs.", highlight: false },
  { text: "En quelques secondes.", highlight: true },
  { text: "", spacer: true },
  { text: "", spacer: true },
  { text: "C'est ça, une entreprise interrogeable.", highlight: true, bold: true, large: true },
  { text: "", spacer: true },
  { text: "Plus de friction.", highlight: false },
  { text: "Plus de dépendance.", highlight: false },
  { text: "Plus de recherche.", highlight: false },
  { text: "", spacer: true },
  { text: "Juste une question.", highlight: false },
  { text: "Et une réponse.", highlight: true, bold: true },
  { text: "", spacer: true },
  { text: "", spacer: true },
  { text: "Cloneia crée un clone mémoire de chaque collaborateur.", highlight: false, bold: true },
  { text: "Et rend cette connaissance accessible à tout moment.", highlight: false },
  { text: "", spacer: true },
  { text: "La mémoire humaine oublie.", highlight: false },
  { text: "La mémoire du clone, jamais.", highlight: true, bold: true },
  { text: "", spacer: true },
  { text: "", spacer: true },
  { text: "Votre entreprise sait tout ce qu'elle a vécu.", highlight: false, large: true },
  { text: "Il suffit de lui demander.", highlight: true, bold: true, large: true },
];

const EntrepriseInterrogeable = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="vision-page">
      <Helmet>
        <title>Et si votre entreprise devenait interrogeable ? | Cloneia</title>
        <meta name="description" content="Une question. Une réponse. Instantanément. Découvrez le concept d'entreprise interrogeable par Cloneia." />
        <link rel="canonical" href="https://cloneia.fr/entreprise-interrogeable" />
        <meta property="og:title" content="Et si votre entreprise devenait interrogeable ? | Cloneia" />
        <meta property="og:description" content="Une question. Une réponse. Instantanément. Découvrez le concept d'entreprise interrogeable." />
        <meta property="og:url" content="https://cloneia.fr/entreprise-interrogeable" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Header />

      {/* Manifesto Content */}
      <section className="pt-32 md:pt-40 pb-24 px-6 md:px-12" data-testid="vision-content">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#71717a] hover:text-[#3b82f6] text-sm mb-12 transition-colors"
              data-testid="vision-breadcrumb"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'accueil
            </Link>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f5f5f5] tracking-tight leading-tight mb-16"
            data-testid="vision-title"
          >
            Et si votre entreprise devenait{' '}
            <span className="text-[#3b82f6]">interrogeable</span> ?
          </motion.h1>

          {/* Manifesto Lines */}
          <div className="space-y-1" data-testid="vision-manifesto">
            {manifestoLines.map((line, index) => {
              if (line.spacer) {
                return <div key={index} className="h-4" />;
              }

              return (
                <motion.p
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-20px" }}
                  transition={{ duration: 0.4, delay: 0.05 }}
                  className={`
                    ${line.large ? 'text-xl md:text-2xl' : 'text-base md:text-lg'}
                    ${line.highlight ? 'text-[#3b82f6]' : 'text-[#a1a1aa]'}
                    ${line.bold ? 'font-semibold' : 'font-normal'}
                    ${line.highlight && line.bold ? 'text-[#f5f5f5]' : ''}
                    leading-relaxed
                  `}
                  style={line.highlight && line.bold ? { color: '#3b82f6' } : {}}
                >
                  {line.text}
                </motion.p>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20 text-center"
            data-testid="vision-cta"
          >
            <div className="h-px w-16 bg-[#3b82f6]/50 mx-auto mb-12" />
            <Link to="/">
              <Button className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-8 py-4 rounded-full font-semibold text-base transition-all group">
                Découvrir Cloneia
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </motion.div>

          {/* Related */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20 pt-12 border-t border-white/5"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-6">Lire aussi</p>
            <div className="space-y-3">
              <Link
                to="/memoire-entreprise-futur-travail"
                className="flex items-center justify-between group p-4 rounded-xl border border-white/5 hover:border-[#3b82f6]/30 bg-[#1a1a2e]/30 hover:bg-[#1a1a2e]/60 transition-all"
                data-testid="vision-related-memoire"
              >
                <span className="text-[#f5f5f5] text-sm font-medium group-hover:text-[#3b82f6] transition-colors">
                  La mémoire d'entreprise : le futur du travail
                </span>
                <ArrowRight className="w-4 h-4 text-[#71717a] group-hover:text-[#3b82f6] group-hover:translate-x-1 transition-all" />
              </Link>
              <Link
                to="/clone-collaborateur-entreprise"
                className="flex items-center justify-between group p-4 rounded-xl border border-white/5 hover:border-[#3b82f6]/30 bg-[#1a1a2e]/30 hover:bg-[#1a1a2e]/60 transition-all"
                data-testid="vision-related-clone"
              >
                <span className="text-[#f5f5f5] text-sm font-medium group-hover:text-[#3b82f6] transition-colors">
                  Cloner la mémoire d'un collaborateur : ce que ça signifie
                </span>
                <ArrowRight className="w-4 h-4 text-[#71717a] group-hover:text-[#3b82f6] group-hover:translate-x-1 transition-all" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <SEOFloatingCTA />
      <Footer />
    </div>
  );
};

export default EntrepriseInterrogeable;
